import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
    Home, FileText, CreditCard, LineChart, UserCircle2,
    User, Mail, Phone, MapPin, Hash,
    Copy, Check, Pencil, LogOut, Trash2, AlertTriangle, Loader2, X,
    MessageSquare, Paperclip, Send, ChevronRight, CheckCircle2,
    Plus, Users, Calendar, Map, Crown, Lock,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import BuddyLogo from '../components/BuddyLogo';
import { getGroupTypeConfig } from "../utils/formatters";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PRIMARY = "#2D5BFF";
const PRIMARY_DARK = "#1B3DBF";
const GRADIENT = `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)`;

const NAV_ITEMS = [
    { icon: Home, label: "Home", key: "home" },
    { icon: FileText, label: "Receipts", key: "receipts" },
    { icon: CreditCard, label: "Cards", key: "cards" },
    { icon: LineChart, label: "Insights", key: "insights" },
    { icon: UserCircle2, label: "Profile", key: "profile" },
];

// ── Group type icon (coloured circle + white icon) ───────────────────────
const GroupTypeIcon = ({ type, emoji, size = 44 }) => {
    const cfg = getGroupTypeConfig(type);
    const Icon = cfg.icon;
    return (
        <div
            className="flex items-center justify-center flex-shrink-0"
            style={{
                width: size, height: size, borderRadius: 14,
                backgroundColor: cfg.bg,
                fontSize: emoji ? Math.round(size * 0.45) : undefined,
            }}
        >
            {emoji
                ? <span style={{ lineHeight: 1 }}>{emoji}</span>
                : <Icon className="w-5 h-5" style={{ color: "#FFFFFF" }} />
            }
        </div>
    );
};

// ── Overlapping member avatar circles ────────────────────────────────────
const MemberAvatars = ({ members = [], size = 28 }) => {
    if (!members.length) return null;
    const shown = members.slice(0, 3);
    return (
        <div className="flex items-center" style={{ marginRight: 4 }}>
            {shown.map((m, i) => {
                const name = m.display_name || "?";
                const initials = name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);
                // Generate a colour from initials for variety
                const colors = ["#7C3AED", "#2563EB", "#0D9488", "#D97706", "#DC2626", "#4F46E5", "#059669"];
                const colorIdx = (name.charCodeAt(0) + (name.charCodeAt(1) || 0)) % colors.length;
                return (
                    <div
                        key={i}
                        className="flex items-center justify-center overflow-hidden flex-shrink-0"
                        style={{
                            width: size, height: size, borderRadius: 999,
                            backgroundColor: m.avatar_url ? "transparent" : colors[colorIdx],
                            border: "2px solid #FFFFFF",
                            marginLeft: i > 0 ? -8 : 0,
                            zIndex: shown.length - i,
                            position: "relative",
                        }}
                    >
                        {m.avatar_url ? (
                            <img src={m.avatar_url} alt={name} className="w-full h-full object-cover" />
                        ) : (
                            <span style={{ color: "#FFFFFF", fontSize: 10, fontWeight: 700, fontFamily: "Manrope, sans-serif" }}>
                                {initials}
                            </span>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

// ── Icon circle (blue tint) ────────────────────────────────────────────────
const IconCircle = ({ icon: Icon, size = 40 }) => (
    <div
        className="flex items-center justify-center flex-shrink-0"
        style={{
            width: size, height: size, borderRadius: 999,
            backgroundColor: "#EEF2FF",
        }}
    >
        <Icon className="w-5 h-5" style={{ color: PRIMARY }} />
    </div>
);

// ── Copy button ────────────────────────────────────────────────────────────
const CopyButton = ({ value }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async (e) => {
        e.stopPropagation();
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            toast.error("Couldn't copy");
        }
    };

    return (
        <button
            onClick={handleCopy}
            className="flex items-center justify-center flex-shrink-0"
            style={{
                width: 36, height: 36, borderRadius: 10,
                backgroundColor: "#F3F4F6",
            }}
            aria-label="Copy"
            data-testid="copy-btn"
        >
            {copied
                ? <Check className="w-4 h-4" style={{ color: "#16A34A" }} />
                : <Copy className="w-4 h-4" style={{ color: "#9CA3AF" }} />}
        </button>
    );
};

// ── Profile row (read-only) ────────────────────────────────────────────────
const ProfileRow = ({ icon, label, value, showCopy = true, isLast = false }) => (
    <div
        style={{
            padding: "14px 0",
            borderBottom: isLast ? "none" : "1px solid #F3F4F6",
        }}
    >
        <div className="flex items-center gap-3">
            <IconCircle icon={icon} />
            <div className="flex-1 min-w-0">
                <p style={{ color: "#9CA3AF", fontSize: 12, fontFamily: "DM Sans, sans-serif", marginBottom: 2 }}>
                    {label}
                </p>
                <p
                    className="truncate profile-no-underline"
                    style={{ color: "#111827", fontSize: 15, fontWeight: 600, fontFamily: "Manrope, sans-serif" }}
                >
                    {value}
                </p>
            </div>
            {showCopy && value && <CopyButton value={value} />}
        </div>
    </div>
);

// ── Editable profile row ───────────────────────────────────────────────────
const EditableRow = ({
    icon, label, value, placeholder, isLast = false,
    editing, onEdit, onSave, onCancel, editValue, setEditValue, saving,
    inputMode = "text",
}) => (
    <div style={{ padding: "14px 0", borderBottom: isLast ? "none" : "1px solid #F3F4F6" }}>
        <div className="flex items-center gap-3">
            <IconCircle icon={icon} />
            <div className="flex-1 min-w-0">
                <p style={{ color: "#9CA3AF", fontSize: 12, fontFamily: "DM Sans, sans-serif", marginBottom: 2 }}>
                    {label}
                </p>
                {editing ? (
                    <div className="flex items-center gap-2 mt-1">
                        <input
                            type="text"
                            inputMode={inputMode}
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            placeholder={placeholder}
                            autoFocus
                            className="flex-1 rounded-lg"
                            style={{
                                height: 38, paddingLeft: 12, paddingRight: 12,
                                border: `1.5px solid ${PRIMARY}`, backgroundColor: "#F5F7FF",
                                fontSize: 16, fontFamily: "DM Sans, sans-serif", outline: "none",
                                color: "#111827",
                            }}
                        />
                        <button
                            onClick={onSave}
                            disabled={saving}
                            className="flex items-center justify-center"
                            style={{
                                width: 36, height: 36, borderRadius: 10,
                                backgroundColor: PRIMARY, color: "#FFFFFF",
                            }}
                        >
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                        </button>
                        <button
                            onClick={onCancel}
                            className="flex items-center justify-center"
                            style={{
                                width: 36, height: 36, borderRadius: 10,
                                backgroundColor: "#F3F4F6", color: "#6B7280",
                            }}
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                ) : value ? (
                    <p
                        className="truncate"
                        style={{ color: "#111827", fontSize: 15, fontWeight: 600, fontFamily: "Manrope, sans-serif" }}
                    >
                        {value}
                    </p>
                ) : (
                    <p style={{ color: "#9CA3AF", fontSize: 14, fontFamily: "DM Sans, sans-serif", fontStyle: "italic" }}>
                        Not added yet
                    </p>
                )}
            </div>
            {!editing && (
                value ? (
                    <div className="flex items-center gap-1">
                        <CopyButton value={value} />
                        <button
                            onClick={onEdit}
                            className="flex items-center justify-center flex-shrink-0"
                            style={{ width: 36, height: 36, borderRadius: 10, backgroundColor: "#F3F4F6" }}
                        >
                            <Pencil className="w-4 h-4" style={{ color: "#9CA3AF" }} />
                        </button>
                    </div>
                ) : (
                    <button
                        onClick={onEdit}
                        className="flex items-center justify-center rounded-lg flex-shrink-0"
                        style={{
                            height: 34, padding: "0 16px",
                            border: `1px solid #E5E7EB`, backgroundColor: "#FFFFFF",
                            color: PRIMARY, fontSize: 13, fontWeight: 600, fontFamily: "DM Sans, sans-serif",
                        }}
                    >
                        Add
                    </button>
                )
            )}
        </div>
    </div>
);

// ── Help & Feedback sheet ─────────────────────────────────────────────────
const FEEDBACK_CATEGORIES = [
    { value: "Review",          placeholder: "What do you love, or what could be better?" },
    { value: "Feature Request", placeholder: "Describe the feature and why it would help you." },
    { value: "Report Issue",    placeholder: "What happened? What were you trying to do when it went wrong?" },
    { value: "Contact Us",      placeholder: "How can we help you?" },
];

const HelpFeedbackSheet = ({ isOpen, onClose }) => {
    const [category, setCategory]         = useState("");
    const [message, setMessage]           = useState("");
    const [attachment, setAttachment]     = useState(null); // { name, type, data (base64 dataURL) }
    const [sending, setSending]           = useState(false);
    const [sent, setSent]                 = useState(false);
    const [error, setError]               = useState("");

    const reset = () => {
        setCategory(""); setMessage(""); setAttachment(null);
        setSending(false); setSent(false); setError("");
    };

    const handleClose = () => { reset(); onClose(); };

    const activePlaceholder = FEEDBACK_CATEGORIES.find(c => c.value === category)?.placeholder
        || "Describe your feedback, issue or question in detail…";

    const handleFile = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) {
            setError("Attachment must be under 5 MB."); return;
        }
        const reader = new FileReader();
        reader.onload = (ev) => {
            setAttachment({ name: file.name, type: file.type, data: ev.target.result });
            setError("");
        };
        reader.readAsDataURL(file);
    };

    const handleSend = async () => {
        if (!category) { setError("Please select a category."); return; }
        if (!message.trim()) { setError("Please describe your feedback."); return; }
        setError(""); setSending(true);
        try {
            await axios.post(`${API}/feedback`, {
                category,
                message: message.trim(),
                attachment_data: attachment?.data || null,
                attachment_name: attachment?.name || null,
                attachment_type: attachment?.type || null,
            }, { withCredentials: true });
            setSent(true);
        } catch {
            setError("Couldn't send your message. Please try again.");
        } finally {
            setSending(false);
        }
    };

    if (!isOpen) return null;

    return (
        <>
            {/* Backdrop */}
            <div
                className="fixed inset-0 z-40"
                style={{ backgroundColor: "rgba(15,23,42,0.4)" }}
                onClick={handleClose}
            />

            {/* Sheet */}
            <div
                className="fixed bottom-0 left-0 right-0 z-50 flex flex-col"
                style={{
                    backgroundColor: "#FFFFFF",
                    borderTopLeftRadius: 24,
                    borderTopRightRadius: 24,
                    maxHeight: "92vh",
                    boxShadow: "0 -8px 32px rgba(15,23,42,0.16)",
                }}
            >
                {/* Drag handle */}
                <div className="flex justify-center pt-3 pb-1">
                    <div style={{ width: 36, height: 4, borderRadius: 99, backgroundColor: "#E5E7EB" }} />
                </div>

                {/* Header */}
                <div className="flex items-start justify-between px-5 py-4">
                    <div>
                        <h2 style={{ color: "#111827", fontSize: 20, fontWeight: 700, fontFamily: "Manrope, sans-serif" }}>
                            Help &amp; Feedback
                        </h2>
                        <p style={{ color: "#6B7280", fontSize: 13, fontFamily: "DM Sans, sans-serif", marginTop: 2 }}>
                            Tell us what's on your mind. We read every message.
                        </p>
                    </div>
                    <button onClick={handleClose} className="flex items-center justify-center"
                        style={{ width: 32, height: 32, borderRadius: 999, backgroundColor: "#F3F4F6", marginTop: 2 }}>
                        <X className="w-4 h-4" style={{ color: "#6B7280" }} />
                    </button>
                </div>

                {/* Scrollable body */}
                <div className="flex-1 overflow-y-auto px-5 pb-6" style={{ overscrollBehavior: "contain" }}>

                    {sent ? (
                        /* ── Success state ── */
                        <div className="flex flex-col items-center text-center py-10">
                            <div className="flex items-center justify-center mb-4"
                                style={{ width: 64, height: 64, borderRadius: 999, backgroundColor: "#F0FDF4" }}>
                                <CheckCircle2 className="w-8 h-8" style={{ color: "#16A34A" }} />
                            </div>
                            <h3 style={{ color: "#111827", fontSize: 18, fontWeight: 700, fontFamily: "Manrope, sans-serif", marginBottom: 8 }}>
                                Message sent!
                            </h3>
                            <p style={{ color: "#6B7280", fontSize: 14, fontFamily: "DM Sans, sans-serif", lineHeight: 1.6 }}>
                                Thanks for reaching out. We'll read your message and get back to you if needed.
                            </p>
                            <button onClick={handleClose}
                                className="mt-6"
                                style={{
                                    height: 46, paddingLeft: 32, paddingRight: 32,
                                    borderRadius: 999, backgroundColor: PRIMARY,
                                    color: "#FFFFFF", fontSize: 14, fontWeight: 700,
                                    fontFamily: "DM Sans, sans-serif",
                                }}>
                                Done
                            </button>
                        </div>
                    ) : (
                        /* ── Form ── */
                        <div className="space-y-5">
                            {/* Category */}
                            <div>
                                <label style={{ color: "#111827", fontSize: 13, fontWeight: 600, fontFamily: "DM Sans, sans-serif" }}>
                                    What's this about? <span style={{ color: "#EF4444" }}>*</span>
                                </label>
                                <div className="relative mt-2">
                                    <select
                                        value={category}
                                        onChange={(e) => { setCategory(e.target.value); setError(""); }}
                                        className="w-full appearance-none"
                                        style={{
                                            height: 48, paddingLeft: 14, paddingRight: 36,
                                            borderRadius: 12, border: "1.5px solid #E5E7EB",
                                            backgroundColor: "#FAFAFA", fontSize: 16,
                                            fontFamily: "DM Sans, sans-serif", color: category ? "#111827" : "#9CA3AF",
                                            outline: "none",
                                        }}
                                    >
                                        <option value="" disabled>Select an option</option>
                                        {FEEDBACK_CATEGORIES.map(c => (
                                            <option key={c.value} value={c.value}>{c.value}</option>
                                        ))}
                                    </select>
                                    <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 rotate-90 pointer-events-none"
                                        style={{ color: "#9CA3AF" }} />
                                </div>
                            </div>

                            {/* Message */}
                            <div>
                                <label style={{ color: "#111827", fontSize: 13, fontWeight: 600, fontFamily: "DM Sans, sans-serif" }}>
                                    Tell us more <span style={{ color: "#EF4444" }}>*</span>
                                </label>
                                <div className="relative mt-2">
                                    <textarea
                                        value={message}
                                        onChange={(e) => { if (e.target.value.length <= 1000) setMessage(e.target.value); }}
                                        placeholder={activePlaceholder}
                                        rows={5}
                                        className="w-full resize-none"
                                        style={{
                                            padding: 14, borderRadius: 12, border: "1.5px solid #E5E7EB",
                                            backgroundColor: "#FAFAFA", fontSize: 16,
                                            fontFamily: "DM Sans, sans-serif", color: "#111827",
                                            outline: "none", lineHeight: 1.6,
                                        }}
                                    />
                                    <span style={{
                                        position: "absolute", bottom: 10, right: 12,
                                        color: "#9CA3AF", fontSize: 11, fontFamily: "DM Sans, sans-serif",
                                    }}>
                                        {message.length}/1000
                                    </span>
                                </div>
                            </div>

                            {/* Attachment */}
                            <div>
                                <label style={{ color: "#111827", fontSize: 13, fontWeight: 600, fontFamily: "DM Sans, sans-serif" }}>
                                    Attachment <span style={{ color: "#9CA3AF", fontWeight: 400 }}>(optional)</span>
                                </label>
                                <label
                                    className="flex items-center gap-3 mt-2 cursor-pointer"
                                    style={{
                                        padding: 14, borderRadius: 12,
                                        border: attachment ? `1.5px solid ${PRIMARY}` : "1.5px dashed #D1D5DB",
                                        backgroundColor: attachment ? "#F0F4FF" : "#FAFAFA",
                                    }}
                                >
                                    <div className="flex items-center justify-center flex-shrink-0"
                                        style={{ width: 36, height: 36, borderRadius: 10, backgroundColor: "#EEF2FF" }}>
                                        <Paperclip className="w-4 h-4" style={{ color: PRIMARY }} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        {attachment ? (
                                            <>
                                                <p className="truncate" style={{ color: "#111827", fontSize: 13, fontWeight: 600, fontFamily: "DM Sans, sans-serif" }}>
                                                    {attachment.name}
                                                </p>
                                                <p style={{ color: "#6B7280", fontSize: 11, fontFamily: "DM Sans, sans-serif" }}>
                                                    Tap to change
                                                </p>
                                            </>
                                        ) : (
                                            <>
                                                <p style={{ color: "#111827", fontSize: 13, fontWeight: 600, fontFamily: "DM Sans, sans-serif" }}>
                                                    Add Attachment
                                                </p>
                                                <p style={{ color: "#9CA3AF", fontSize: 11, fontFamily: "DM Sans, sans-serif" }}>
                                                    Images (PNG, JPG) up to 5 MB
                                                </p>
                                            </>
                                        )}
                                    </div>
                                    {attachment && (
                                        <button
                                            onClick={(e) => { e.preventDefault(); setAttachment(null); }}
                                            className="flex-shrink-0 flex items-center justify-center"
                                            style={{ width: 24, height: 24, borderRadius: 999, backgroundColor: "#E5E7EB" }}>
                                            <X className="w-3 h-3" style={{ color: "#6B7280" }} />
                                        </button>
                                    )}
                                    <input
                                        type="file" accept="image/png,image/jpeg"
                                        className="hidden" onChange={handleFile}
                                    />
                                </label>
                            </div>

                            {/* Error */}
                            {error && (
                                <p style={{ color: "#EF4444", fontSize: 13, fontFamily: "DM Sans, sans-serif" }}>
                                    {error}
                                </p>
                            )}

                            {/* Privacy note */}
                            <p className="text-center" style={{ color: "#9CA3AF", fontSize: 11, fontFamily: "DM Sans, sans-serif" }}>
                                🔒 Your information is safe and used only to help you.
                            </p>

                            {/* Send button */}
                            <button
                                onClick={handleSend}
                                disabled={sending}
                                className="w-full flex items-center justify-center gap-2"
                                style={{
                                    height: 52, borderRadius: 999,
                                    background: `linear-gradient(180deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)`,
                                    color: "#FFFFFF", fontSize: 15, fontWeight: 700,
                                    fontFamily: "DM Sans, sans-serif",
                                    opacity: sending ? 0.7 : 1,
                                    boxShadow: "0 8px 20px rgba(45,91,255,0.28)",
                                }}
                            >
                                {sending
                                    ? <Loader2 className="w-5 h-5 animate-spin" />
                                    : <Send className="w-4 h-4" />}
                                {sending ? "Sending…" : "Send Message"}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

// ── Main page ──────────────────────────────────────────────────────────────
export default function ProfilePage() {
    const { user: authUser, logout } = useAuth();
    const navigate = useNavigate();
    const [isMobile, setIsMobile] = useState(false);
    const [loading, setLoading] = useState(true);
    const [profile, setProfile] = useState(null);
    const [editField, setEditField] = useState(null); // "mobile" | "address" | "postcode"
    const [editValue, setEditValue] = useState("");
    const [saving, setSaving] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [showFeedback, setShowFeedback] = useState(false);
    const [groups, setGroups] = useState([]);
    const [groupsLoading, setGroupsLoading] = useState(true);

    useEffect(() => {
        const check = () => setIsMobile(window.innerWidth < 768);
        check();
        window.addEventListener("resize", check);
        return () => window.removeEventListener("resize", check);
    }, []);

    const fetchProfile = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
            setProfile(res.data);
        } catch (err) {
            if (err.response?.status === 401) navigate("/login", { replace: true });
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    useEffect(() => { fetchProfile(); }, [fetchProfile]);

    // ── Fetch groups ─────────────────────────────────────────────────────────
    const fetchGroups = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/groups?active_only=false`, { withCredentials: true });
            setGroups(res.data.groups || []);
        } catch {
            // Silently fail — groups section just won't show
        } finally {
            setGroupsLoading(false);
        }
    }, []);

    useEffect(() => { fetchGroups(); }, [fetchGroups]);

    // Capitalise first letter of each word — safety net for existing DB records.
    const toTitleCase = (s) => (s || '').replace(/\b[a-z]/g, (c) => c.toUpperCase());

    const rawName = `${profile?.first_name || authUser?.first_name || ""} ${profile?.last_name || authUser?.last_name || ""}`.trim() || "User";
    const userName = toTitleCase(rawName);
    const email = profile?.email || authUser?.email || "";
    const avatarUrl = profile?.avatar_url || authUser?.avatar_url;
    const initials = userName.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2);
    const createdAt = profile?.created_at;
    const memberSince = createdAt
        ? new Date(createdAt).toLocaleDateString("en-GB", { month: "short", year: "numeric" })
        : null;

    // ── Edit handlers ────────────────────────────────────────────────────────
    const startEdit = (field) => {
        setEditField(field);
        setEditValue(profile?.[field] || "");
    };

    const cancelEdit = () => { setEditField(null); setEditValue(""); };

    const saveField = async () => {
        if (!editField || !editValue.trim()) return;
        setSaving(true);
        try {
            const res = await axios.put(
                `${API}/profile`,
                { [editField]: editValue.trim() },
                { withCredentials: true }
            );
            if (res.data.success) {
                setProfile((prev) => ({ ...prev, [editField]: editValue.trim() }));
                toast.success("Saved");
                setEditField(null);
                setEditValue("");
            }
        } catch {
            toast.error("Couldn't save");
        } finally {
            setSaving(false);
        }
    };

    // ── Logout ───────────────────────────────────────────────────────────────
    const handleLogout = async () => {
        try {
            await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
        } catch { /* ignore */ }
        logout?.();
        navigate("/login", { replace: true });
    };

    // ── Delete account ───────────────────────────────────────────────────────
    const handleDeleteAccount = async () => {
        setDeleting(true);
        try {
            const res = await axios.delete(`${API}/account`, { withCredentials: true });
            if (res.data.success) {
              //  toast.success("Account deleted");
            }
            logout?.();
            navigate("/login", { replace: true });
        } catch (err) {
            // If 401/404 the account/session is already gone — redirect anyway
            if (err.response?.status === 401 || err.response?.status === 404) {
                logout?.();
                navigate("/login", { replace: true });
            } else {
                toast.error("Couldn't delete account. Please try again.");
            }
        } finally {
            setDeleting(false);
        }
    };

    const onNavClick = (key) => {
        if (key === "home") navigate("/dashboard");
        else if (key === "receipts") navigate("/receipts");
        else if (key === "cards") navigate("/cards");
        else if (key === "insights") navigate("/insights");
        // profile = current page
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#F9FAFB" }}>
                <Loader2 className="w-8 h-8 animate-spin" style={{ color: PRIMARY }} />
            </div>
        );
    }


    // Shared content cards (used by both mobile and desktop)
    const profileContent = (paddingBottom) => (
        <div style={{ paddingBottom }}>
            {/* Personal Information card */}
            <div
                style={{
                    backgroundColor: "#FFFFFF",
                    borderRadius: 20,
                    padding: "6px 18px 8px",
                    boxShadow: "0 4px 16px rgba(15,23,42,0.06)",
                }}
                data-testid="personal-info-card"
            >
                <div className="py-3">
                    <p style={{ color: "#111827", fontSize: 16, fontWeight: 700, fontFamily: "Manrope, sans-serif" }}>
                        Personal Information
                    </p>
                </div>
                <ProfileRow icon={User} label="Full Name" value={userName} />
                <ProfileRow icon={Mail} label="Email Address" value={email} />
                <EditableRow icon={Phone} label="Mobile Number" value={profile?.mobile || ""} placeholder="+44 7XXX XXXXXX" inputMode="tel" editing={editField === "mobile"} editValue={editValue} setEditValue={setEditValue} onEdit={() => startEdit("mobile")} onSave={saveField} onCancel={cancelEdit} saving={saving} />
                <EditableRow icon={MapPin} label="Address Line 1" value={profile?.address_line1 || ""} placeholder="Your address" editing={editField === "address_line1"} editValue={editValue} setEditValue={setEditValue} onEdit={() => startEdit("address_line1")} onSave={saveField} onCancel={cancelEdit} saving={saving} />
                <EditableRow icon={Hash} label="Postcode (UK)" value={(profile?.postcode || "").toUpperCase()} placeholder="e.g. LS1 2AB" isLast editing={editField === "postcode"} editValue={editValue} setEditValue={setEditValue} onEdit={() => startEdit("postcode")} onSave={saveField} onCancel={cancelEdit} saving={saving} />
            </div>

            {/* Buddy Circles section */}
            <div className="flex items-center justify-between mt-6 mb-3 px-1">
                <h2 style={{ color: "#111827", fontSize: 16, fontWeight: 700, fontFamily: "Manrope, sans-serif" }}>
                    Buddy Circles
                </h2>
            </div>

            <div
                style={{
                    backgroundColor: "#FFFFFF",
                    borderRadius: 20,
                    padding: "4px 18px",
                    boxShadow: "0 4px 16px rgba(15,23,42,0.06)",
                }}
                data-testid="groups-section"
            >
                {groupsLoading ? (
                    <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-5 h-5 animate-spin" style={{ color: PRIMARY }} />
                    </div>
                ) : (
                    <>
                        {groups.map((group, idx) => {
                            const cfg = getGroupTypeConfig(group.type);
                            const isClosed = group.status === "closed";
                            return (
                                <button
                                    key={group.id}
                                    onClick={() => navigate(`/groups/${group.id}`)}
                                    className="w-full flex items-center gap-3 py-3"
                                    style={{
                                        borderBottom: (idx < groups.length - 1 || true) ? "1px solid #F3F4F6" : "none",
                                        opacity: isClosed ? 0.65 : 1,
                                    }}
                                >
                                    <GroupTypeIcon type={group.type} emoji={group.emoji} />
                                    <div className="flex-1 min-w-0 text-left">
                                        <div className="flex items-center gap-1.5">
                                            <p
                                                className="truncate"
                                                style={{ color: "#111827", fontSize: 15, fontWeight: 600, fontFamily: "Manrope, sans-serif" }}
                                            >
                                                {group.name}
                                            </p>
                                            {isClosed && <Lock className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "#D97706" }} />}
                                        </div>
                                        <p style={{ color: "#9CA3AF", fontSize: 13, fontFamily: "DM Sans, sans-serif", marginTop: 2 }}>
                                            {isClosed ? "Closed" : `${cfg.label} · ${group.member_count} member${group.member_count !== 1 ? "s" : ""}`}
                                        </p>
                                    </div>
                                    <MemberAvatars members={group.member_previews || []} />
                                    <ChevronRight className="w-5 h-5 flex-shrink-0" style={{ color: "#D1D5DB" }} />
                                </button>
                            );
                        })}

                        {/* Create a circle row */}
                        <button
                            onClick={() => navigate("/groups/create")}
                            className="w-full flex items-center gap-3 py-3"
                        >
                            <div
                                className="flex items-center justify-center flex-shrink-0"
                                style={{
                                    width: 44, height: 44, borderRadius: 14,
                                    backgroundColor: "#F3F4F6",
                                }}
                            >
                                <Plus className="w-5 h-5" style={{ color: PRIMARY }} />
                            </div>
                            <p style={{ color: PRIMARY, fontSize: 15, fontWeight: 600, fontFamily: "Manrope, sans-serif" }}>
                                Create a circle
                            </p>
                        </button>
                    </>
                )}
            </div>

            {/* Help & Feedback + Account card */}
            <div className="mt-4" style={{ backgroundColor: "#FFFFFF", borderRadius: 20, padding: "4px 18px", boxShadow: "0 4px 16px rgba(15,23,42,0.06)" }} data-testid="account-section">
                <button onClick={() => setShowFeedback(true)} className="w-full flex items-center gap-3 py-3" style={{ borderBottom: "1px solid #F3F4F6" }} data-testid="help-feedback-btn">
                    <IconCircle icon={MessageSquare} />
                    <div className="flex-1 min-w-0 text-left">
                        <p style={{ color: "#111827", fontSize: 15, fontWeight: 600, fontFamily: "Manrope, sans-serif" }}>Help &amp; Feedback</p>
                        <p style={{ color: "#9CA3AF", fontSize: 13, fontFamily: "DM Sans, sans-serif", marginTop: 2 }}>Report issues, request features</p>
                    </div>
                    <ChevronRight className="w-5 h-5" style={{ color: "#D1D5DB" }} />
                </button>

                <button onClick={handleLogout} className="w-full flex items-center gap-3 py-3" data-testid="logout-btn">
                    <div className="flex items-center justify-center flex-shrink-0" style={{ width: 40, height: 40, borderRadius: 999, backgroundColor: "#FEE2E2" }}>
                        <LogOut className="w-5 h-5" style={{ color: "#EF4444" }} />
                    </div>
                    <span style={{ color: "#EF4444", fontSize: 15, fontWeight: 600, fontFamily: "Manrope, sans-serif" }}>Log out</span>
                </button>
            </div>

            {/* Delete account (separate, subtle) */}
            <div className="mt-4" style={{ backgroundColor: "#FFFFFF", borderRadius: 20, padding: "4px 18px", boxShadow: "0 4px 16px rgba(15,23,42,0.06)" }} data-testid="danger-zone">
                {!confirmDelete ? (
                    <button onClick={() => setConfirmDelete(true)} className="w-full flex items-center gap-3 py-3" data-testid="delete-account-btn">
                        <div className="flex items-center justify-center flex-shrink-0" style={{ width: 40, height: 40, borderRadius: 999, backgroundColor: "#FEE2E2" }}>
                            <Trash2 className="w-5 h-5" style={{ color: "#EF4444" }} />
                        </div>
                        <span style={{ color: "#EF4444", fontSize: 15, fontWeight: 600, fontFamily: "Manrope, sans-serif" }}>Delete account</span>
                    </button>
                ) : (
                    <div className="py-3">
                        <div className="flex items-center gap-2 mb-3">
                            <AlertTriangle className="w-5 h-5 flex-shrink-0" style={{ color: "#EF4444" }} />
                            <p style={{ color: "#EF4444", fontSize: 13, fontWeight: 600, fontFamily: "Manrope, sans-serif" }}>This will permanently delete your account, receipts, and cards.</p>
                        </div>
                        <div className="flex gap-2">
                            <button onClick={() => setConfirmDelete(false)} className="flex-1 py-2.5 rounded-xl" style={{ border: "1px solid #E5E7EB", color: "#374151", fontSize: 14, fontWeight: 600, fontFamily: "DM Sans, sans-serif" }}>Cancel</button>
                            <button onClick={handleDeleteAccount} disabled={deleting} className="flex-1 py-2.5 rounded-xl flex items-center justify-center gap-2" style={{ backgroundColor: "#EF4444", color: "#FFFFFF", fontSize: 14, fontWeight: 600, fontFamily: "DM Sans, sans-serif" }}>
                                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                {deleting ? "Deleting…" : "Yes, delete"}
                            </button>
                        </div>
                    </div>
                )}
            </div>
            <p className="text-center mt-6" style={{ color: "#D1D5DB", fontSize: 11, fontFamily: "DM Sans, sans-serif" }}>My Buddy v1.3{memberSince ? ` · Member since ${memberSince}` : ""}</p>
        </div>
    );

    // ── MOBILE LAYOUT ────────────────────────────────────────────────────────
    if (isMobile) {
        return (
            <div className="min-h-screen flex flex-col" style={{ backgroundColor: "#F9FAFB" }} data-testid="profile-page">
                <header style={{ background: GRADIENT, padding: "16px 20px 40px 20px", borderBottomLeftRadius: 28, borderBottomRightRadius: 28 }}>
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 style={{ color: "#FFFFFF", fontSize: 26, fontWeight: 700, fontFamily: "Manrope, sans-serif" }}>Profile</h1>
                            <p style={{ color: "rgba(255,255,255,0.75)", fontSize: 14, fontFamily: "DM Sans, sans-serif", marginTop: 4 }}>Your account &amp; sharing</p>
                        </div>
                        <div className="flex items-center justify-center overflow-hidden" style={{ width: 56, height: 56, borderRadius: 999, backgroundColor: avatarUrl ? "transparent" : "rgba(255,255,255,0.15)", color: "#FFFFFF", fontWeight: 700, fontSize: 18, fontFamily: "Manrope, sans-serif" }}>
                            {avatarUrl ? <img src={avatarUrl} alt={userName} className="w-full h-full object-cover" /> : initials}
                        </div>
                    </div>
                </header>
                <div className="flex-1 px-4" style={{ marginTop: -20, paddingBottom: "calc(100px + env(safe-area-inset-bottom, 34px))" }}>
                    {profileContent(0)}
                </div>
                <nav className="fixed bottom-0 left-0 right-0 flex justify-around items-center" style={{ backgroundColor: "#FFFFFF", borderTop: "1px solid #F3F4F6", paddingTop: 10, paddingBottom: "calc(12px + env(safe-area-inset-bottom, 0px))", zIndex: 50 }} data-testid="bottom-nav">
                    {NAV_ITEMS.map((item) => {
                        const active = item.key === "profile";
                        return (
                            <button key={item.key} onClick={() => onNavClick(item.key)} className="flex flex-col items-center gap-1">
                                <item.icon className="w-5 h-5" style={{ color: active ? PRIMARY : "#9CA3AF" }} fill={active ? PRIMARY : "none"} />
                                <span style={{ color: active ? PRIMARY : "#9CA3AF", fontSize: 11, fontFamily: "DM Sans, sans-serif", fontWeight: active ? 600 : 500 }}>{item.label}</span>
                            </button>
                        );
                    })}
                </nav>
                <HelpFeedbackSheet isOpen={showFeedback} onClose={() => setShowFeedback(false)} />
            </div>
        );
    }

    // ── DESKTOP LAYOUT ───────────────────────────────────────────────────────
    return (
        <div className="h-screen flex overflow-hidden" style={{ backgroundColor: "#F3F4F6" }} data-testid="profile-page">
            <style>{`
              .profile-no-underline {
                text-decoration: none !important;
                -webkit-text-decorations-in-effect: none !important;
              }
            `}</style>
            {/* Sidebar */}
            <aside className="flex flex-col" style={{ width: 240, backgroundColor: "#F9FAFB", borderRight: "1px solid #F3F4F6" }}>
                <div className="flex items-center gap-2 px-5 pt-6 pb-8">
                    <BuddyLogo size={28} />
                    <span style={{ color: "#111827", fontSize: 18, fontWeight: 600, fontFamily: "Manrope, sans-serif" }}>My Buddy</span>
                </div>
                <nav className="flex-1 px-3">
                    {NAV_ITEMS.map((item) => {
                        const active = item.key === "profile";
                        return (
                            <button key={item.key} onClick={() => onNavClick(item.key)} className="w-full flex items-center gap-3 px-4 py-3 mb-1 transition-all" style={{ backgroundColor: active ? "#E0E7FF" : "transparent", color: active ? PRIMARY : "#6B7280", borderRadius: 10, fontSize: 14, fontWeight: active ? 600 : 500, fontFamily: "DM Sans, sans-serif" }} data-testid={`sidebar-nav-${item.key}`}>
                                <item.icon className="w-5 h-5" />
                                {item.label}
                            </button>
                        );
                    })}
                </nav>
                <div className="p-4 border-t" style={{ borderColor: "#F3F4F6" }}>
                    <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center overflow-hidden flex-shrink-0" style={{ width: 40, height: 40, borderRadius: 999, backgroundColor: avatarUrl ? "transparent" : PRIMARY, color: "#FFFFFF", fontWeight: 700, fontSize: 14, fontFamily: "Manrope, sans-serif" }}>
                            {avatarUrl ? <img src={avatarUrl} alt={userName} className="w-full h-full object-cover" /> : (userName || "U").charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                            <p className="truncate" style={{ color: "#111827", fontSize: 14, fontWeight: 600, fontFamily: "Manrope, sans-serif" }}>{userName}</p>
                            {email ? <p className="truncate profile-no-underline" style={{ color: "#6B7280", fontSize: 12, fontFamily: "DM Sans, sans-serif" }}>{email}</p> : null}
                        </div>
                    </div>
                </div>
            </aside>

            {/* Main */}
            <main className="flex-1 overflow-y-auto">
                {/* Blue gradient header — desktop */}
                <div style={{ background: GRADIENT, padding: "28px 40px 24px" }}>
                    <div className="flex items-start justify-between">
                        <div>
                            <h1 style={{ color: "#FFFFFF", fontSize: 26, fontWeight: 800, fontFamily: "Manrope, sans-serif", letterSpacing: -0.5, lineHeight: 1.2 }}>
                                Profile
                            </h1>
                            <p style={{ color: "rgba(255,255,255,0.78)", fontSize: 13, fontFamily: "DM Sans, sans-serif", marginTop: 2 }}>
                                Your personal details
                            </p>
                        </div>
                        <div
                            className="flex items-center justify-center overflow-hidden flex-shrink-0"
                            style={{
                                width: 48, height: 48, borderRadius: 999,
                                backgroundColor: avatarUrl ? "transparent" : "rgba(255,255,255,0.2)",
                                color: "#FFFFFF", fontWeight: 700, fontSize: 16, fontFamily: "Manrope, sans-serif",
                            }}
                        >
                            {avatarUrl ? <img src={avatarUrl} alt={userName} className="w-full h-full object-cover" /> : initials}
                        </div>
                    </div>
                </div>
                <div style={{ maxWidth: 640, margin: "0 auto", padding: "30px 40px 40px" }}>
                    {profileContent(0)}
                </div>
            </main>
            <HelpFeedbackSheet isOpen={showFeedback} onClose={() => setShowFeedback(false)} />
        </div>
    );
}